// pkg135 — Not Invited / Awaiting Login split (D-135b): StatusBadge three states + stats row goes 4-up (Total / Active / Awaiting Login / Not Invited — awaiting now counts only INVITED not-yet-logged-in clients) — delivered 2026-07-28
// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg67 — owner-only "Finish setting up" card above the stats: heuristic-driven rows (contact info → Firm Settings, review modules → Modules, first client → New Client) that disappear as completed; the whole card vanishes when done. No new columns — state is derived (D-67k) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';


export default function AdminDashboardPage() {
  const { adminUser } = useAdminAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // pkg67 — setup-card inputs. Firm settings load best-effort: a fetch
  // failure means "no card" rather than a broken dashboard (never block the
  // page on it). null = still loading / unavailable → card hidden.
  const [firmSettings, setFirmSettings] = useState(null);


  useEffect(() => {
    adminApi.listClients().
    then((data) => {setClients(data);setLoading(false);}).
    catch((err) => {setError(err.message);setLoading(false);});
    adminApi.getFirmSettings().
    then((s) => setFirmSettings(s || {})).
    catch(() => setFirmSettings(null));
  }, []);

  const active = clients.filter((c) => !c.must_change_password).length;
  // pkg135 — pending now means INVITED and not yet logged in; never-invited
  // clients get their own count so the card doubles as a to-do list.
  const pending = clients.filter((c) => c.must_change_password && c.invited_at).length;
  const notInvited = clients.filter((c) => c.must_change_password && !c.invited_at).length;
  const recent = [...clients].
  sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).
  slice(0, 10);

  // pkg135 — three-state badge (D-135b): Active = the client has logged in
  // and changed their password (covers hand-delivered credentials with no
  // auth changes); among not-yet-logged-in clients, invited_at from
  // /send-welcome splits the honest "Not Invited" from "Awaiting Login".
  function StatusBadge({ client }) {
    const state = !client.must_change_password
      ? { label: 'Active',        bg: '#f0faf5', fg: '#1e7e5e', bd: '#b7e3cf' }
      : client.invited_at
      ? { label: 'Awaiting Login', bg: '#fff8e1', fg: '#b7870a', bd: '#f5d87a' }
      : { label: 'Not Invited',    bg: '#f2f2f2', fg: '#666666', bd: '#d9d9d9' };
    return (
      <span style={{
        fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3,
        background: state.bg,
        color: state.fg,
        border: `1px solid ${state.bd}`,
        whiteSpace: 'nowrap'
      }}>
        {state.label}
      </span>);

  }

  // pkg67 — the setup checklist, derived entirely from existing data:
  //   * contact info done = phone OR contact email set in Firm Settings
  //   * modules reviewed = no direct signal, so it stays a suggestion row
  //     that never blocks completion — it renders only while OTHER rows are
  //     open (the card exists to guide the first hour, not to nag forever)
  //   * first client done = any client exists
  // Owner-only: staff can't fix firm settings, so the card would only
  // frustrate them. The card disappears entirely once the two actionable
  // heuristics pass — nothing is stored, nothing to migrate.
  const contactDone = !!(firmSettings && (firmSettings.phone || firmSettings.client_contact_email));
  const clientDone = clients.length > 0;
  const showSetupCard =
    adminUser?.role === 'owner' &&
    firmSettings !== null &&
    !loading &&
    (!contactDone || !clientDone);

  function SetupRow({ done, label, hint, to, cta }) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '8px 10px',
        background: '#fff', border: '1px solid #eee', borderRadius: 4,
      }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
          <span style={{ color: done ? '#1e7e5e' : '#bbb', fontWeight: 700, flexShrink: 0 }}>
            {done ? '✓' : '○'}
          </span>
          <span style={{
            color: done ? '#aaa' : '#0F1838',
            textDecoration: done ? 'line-through' : 'none',
            fontWeight: done ? 400 : 500,
          }}>
            {label}
            {!done && hint && <span style={{ color: '#888', fontWeight: 400 }}> — {hint}</span>}
          </span>
        </span>
        {!done && to && (
          <Link to={to} className="btn btn-sm" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{cta}</Link>
        )}
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">Attorney Dashboard</h1>
      <p className="page-subtitle">Overview of all client portal accounts.</p>

      {/* pkg67 — Finish setting up (owner-only, disappears when complete) */}
      {showSetupCard && (
        <div className="card" style={{ border: '2px solid #0F1838', marginBottom: 24 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            flexWrap: 'wrap', gap: 8, marginBottom: 10,
          }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>
              Finish setting up{firmSettings.firm_name ? ` ${firmSettings.firm_name}` : ' your firm'}
            </span>
            <span style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888' }}>
              {2 + (contactDone ? 1 : 0) + (clientDone ? 1 : 0)} of 5 done
            </span>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <SetupRow done label="Confirm your email" />
            <SetupRow done label="Pick your practice areas" />
            <SetupRow done={contactDone} label="Add your firm's contact info"
              hint="phone, email, and address appear on client emails and documents"
              to="/admin/dashboard/firm-settings" cta="Firm Settings" />
            <SetupRow done={false} label="Review your modules"
              hint="we pre-enabled a recommended set — add or remove anytime"
              to="/admin/dashboard/modules" cta="Modules" />
            <SetupRow done={clientDone} label="Add your first client"
              to="/admin/create-client" cta="New Client" />
          </div>
        </div>
      )}

      {/* Stats — 3 col desktop, 3 col mobile (small numbers fit) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
        {[
        { label: 'Total Clients', val: clients.length, color: '#0F1838' },
        { label: 'Active', val: active, color: '#1e7e5e' },
        { label: 'Awaiting Login', val: pending, color: '#b7870a' },
        { label: 'Not Invited', val: notInvited, color: '#666666' }].
        map(({ label, val, color }) =>
        <div key={label} className="card" style={{ marginBottom: 0, textAlign: 'center', padding: '16px 8px' }}>
            <div style={{ fontSize: 28, fontWeight: 600, color, marginBottom: 2 }}>{val}</div>
            <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3 }}>{label}</div>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        <Link to="/admin/dashboard/log-time" className="btn btn-success">+ Log Time</Link>
        <Link to="/admin/create-client" className="btn btn-primary">+ New Client</Link>
        <Link to="/admin/clients" className="btn">All Clients</Link>
        <Link to="/admin/dashboard/mass-email" className="btn">Mass Email</Link>
      </div>

      {/* Recent clients */}
      <div className="section-head" style={{ marginTop: 0 }}>Recent Clients</div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', fontSize: 13 }}>Loading...</div>}

      {!loading && !error &&
      <>
          {/* Desktop table — matches All Clients table.
              Note: the Username column was removed (consistent with All
              Clients — username is shown on the client detail page only).
              The card carries `client-list-table` so the same squish-zone
              media query in index.css enables horizontal scroll between
              769–900px. */}
          <div className="desktop-only">
            <div className="card client-list-table" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0F1838' }}>
                    {['Name', 'Email', 'Status', 'Created'].map((h) =>
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 }}>{h}</th>
                  )}
                    <th style={{ padding: '9px 14px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 &&
                <tr><td colSpan={5} style={{ padding: '24px 16px', textAlign: 'center', fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>
                      No clients yet. <Link to="/admin/create-client" style={{ color: '#0F1838' }}>Create your first client account.</Link>
                    </td></tr>
                }
                  {recent.map((c, i) =>
                <tr key={c.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px 14px', fontSize: 14, fontWeight: 500 }}>{c.name}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555' }}>{c.email}</td>
                      <td style={{ padding: '10px 14px' }}><StatusBadge client={c} /></td>
                      <td style={{ padding: '10px 14px', fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', whiteSpace: 'nowrap' }}>
                        {new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Link to={`/admin/clients/${c.id}`} className="btn btn-sm">Manage</Link>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="mobile-only">
            {recent.length === 0 &&
          <div className="card" style={{ textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                No clients yet. <Link to="/admin/create-client" style={{ color: '#0F1838' }}>Create your first.</Link>
              </div>
          }
            {recent.map((c) =>
          <div key={c.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: '#555', fontFamily: 'Arial, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 8 }}>{c.email}</div>
                    <StatusBadge client={c} />
                  </div>
                  <Link to={`/admin/clients/${c.id}`} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>Manage</Link>
                </div>
              </div>
          )}
            {clients.length > 10 &&
          <Link to="/admin/clients" className="btn" style={{ width: '100%', textAlign: 'center', display: 'block' }}>
                View All {clients.length} Clients →
              </Link>
          }
          </div>
        </>
      }
    </div>);

}